/**
 * Shared sticky wizard header: title + step indicator + serial ID badge.
 * Pins flush below Layout's logo bar via StickyBar (BELOW_LOGO_BAR_STICKY).
 * Used by both NewProcurement and ContinueProcurement so the two pages
 * can't drift apart, and measures its own rendered height into
 * WizardContext so a step needing a secondary sticky row of its own (e.g.
 * StepRisk's score header) can pin flush beneath it with zero pre-pin
 * travel, at both the mobile and desktop logo-bar offsets.
 */
import { useLayoutEffect, useRef } from 'react';
import StickyBar from '@/components/StickyBar';
import StepIndicator from '@/components/StepIndicator';
import { useWizard, WIZARD_STEPS } from './WizardContext';

const WizardHeader = ({ title }) => {
  const { currentStep, serialId, setWizardHeaderHeight, stepStates } = useWizard();
  const barRef = useRef(null);

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const updateHeight = () => setWizardHeaderHeight(el.offsetHeight);
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, [setWizardHeaderHeight]);

  return (
    <StickyBar ref={barRef} className="px-6 md:px-12 flex items-center gap-4">
      <h1 className="hidden md:block text-lg font-semibold text-zinc-900 whitespace-nowrap">{title}</h1>
      <div className="flex-1 min-w-0">
        <StepIndicator steps={WIZARD_STEPS} currentStep={currentStep} stepStates={stepStates} />
      </div>
      {serialId && (
        <div className="bg-[#0055FF]/5 border border-[#0055FF]/20 px-3 py-1 shrink-0">
          <span className="text-[#0055FF] font-mono text-xs font-semibold" data-testid="procurement-serial-badge">
            {serialId}
          </span>
        </div>
      )}
    </StickyBar>
  );
};

export default WizardHeader;
